<template>
  <div class="customer-profile-page">
    <div class="customer-profile-shell">
      <section class="customer-hero">
        <v-btn
          icon
          class="customer-hero__back"
          title="Zurück zur Benutzersuche"
          aria-label="Zurück zur Benutzersuche"
          @click="$router.push('/dashboard/user/search')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <div class="customer-hero__identity">
          <v-avatar
            :size="isMobile ? 72 : 88"
            color="#26322b"
            class="customer-hero__avatar"
            :class="{ 'customer-hero__avatar--interactive': user._id }"
            :role="user._id ? 'button' : undefined"
            :tabindex="user._id ? 0 : -1"
            :title="user._id ? 'Profilbild vergrössern' : undefined"
            :aria-label="user._id ? `Profilbild von ${fullName} vergrössern` : undefined"
            @click="openProfileImage"
            @keydown.enter="openProfileImage"
            @keydown.space.prevent="openProfileImage">
            <img v-if="user._id" :src="profileImage" :alt="fullName" />
            <v-icon v-else size="36">mdi-account-outline</v-icon>
            <span v-if="user._id" class="customer-hero__avatar-expand" aria-hidden="true">
              <v-icon x-small>mdi-magnify-plus-outline</v-icon>
            </span>
          </v-avatar>
          <div class="customer-hero__copy">
            <span class="customer-eyebrow">KUNDENAKTE</span>
            <h1>{{ user._id ? fullName : "Kundenprofil wird geladen" }}</h1>
            <div v-if="user._id" class="customer-hero__meta">
              <span v-if="user.email">
                <v-icon small>mdi-email-outline</v-icon>
                {{ user.email }}
              </span>
              <span v-if="user.telefon">
                <v-icon small>mdi-phone-outline</v-icon>
                {{ user.telefon }}
              </span>
              <span v-if="tacMembershipStart">
                <v-icon small>mdi-account-clock-outline</v-icon>
                Mitglied seit {{ $moment(tacMembershipStart).format("MM.YYYY") }}
              </span>
            </div>
            <div v-if="user._id" class="customer-hero__badges">
              <span :class="{ 'identity-badge--success': user.verifiedEmail }" class="identity-badge">
                <v-icon x-small>{{ user.verifiedEmail ? "mdi-check-decagram" : "mdi-email-alert-outline" }}</v-icon>
                {{ user.verifiedEmail ? "E-Mail verifiziert" : "E-Mail nicht verifiziert" }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="user._id" class="customer-hero__actions">
          <v-btn v-if="user.telefon" icon :href="`tel:${user.telefon}`" title="Kunden anrufen" aria-label="Kunden anrufen">
            <v-icon>mdi-phone-outline</v-icon>
          </v-btn>
          <v-btn v-if="user.email" icon :href="`mailto:${user.email}`" title="E-Mail schreiben" aria-label="E-Mail schreiben">
            <v-icon>mdi-email-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="userService.hasRight('u_delete')"
            icon
            color="error"
            title="Benutzer löschen"
            aria-label="Benutzer löschen"
            @click="deleteUser">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </section>

      <section v-if="user._id && !user.hasInternalAccess" class="customer-quick-actions" aria-label="Schnellaktionen für diesen Kunden">
        <button v-if="canManageAppointments" type="button" class="customer-quick-action" @click="planAppointment">
          <span class="customer-quick-action__icon customer-quick-action__icon--green">
            <v-icon>mdi-calendar-plus</v-icon>
          </span>
          <span>
            <strong>Termin planen</strong>
            <small>Kunde direkt übernehmen</small>
          </span>
          <v-icon class="customer-quick-action__arrow">mdi-chevron-right</v-icon>
        </button>

        <button type="button" class="customer-quick-action" @click="activeGroup = 'training'">
          <span class="customer-quick-action__icon customer-quick-action__icon--blue">
            <v-icon>mdi-dumbbell</v-icon>
          </span>
          <span>
            <strong>Trainingspläne</strong>
            <small>Erstellen und bearbeiten</small>
          </span>
          <v-icon class="customer-quick-action__arrow">mdi-chevron-right</v-icon>
        </button>

        <button v-if="canCreateInteraction" type="button" class="customer-quick-action" @click="openInteraction">
          <span class="customer-quick-action__icon customer-quick-action__icon--purple">
            <v-icon>mdi-message-plus-outline</v-icon>
          </span>
          <span>
            <strong>Interaktion</strong>
            <small>Kontakt dokumentieren</small>
          </span>
          <v-icon class="customer-quick-action__arrow">mdi-plus</v-icon>
        </button>

        <button type="button" class="customer-quick-action" @click="openAnamneseManager">
          <span class="customer-quick-action__icon customer-quick-action__icon--teal">
            <v-icon>mdi-clipboard-pulse-outline</v-icon>
          </span>
          <span>
            <strong>Anamnese</strong>
            <small>Öffnen oder neu erfassen</small>
          </span>
          <v-icon class="customer-quick-action__arrow">mdi-chevron-right</v-icon>
        </button>

        <button v-if="canManageContracts" type="button" class="customer-quick-action" @click="openMembershipAction">
          <span class="customer-quick-action__icon customer-quick-action__icon--amber">
            <v-icon>{{ currentTacMembership ? "mdi-trending-up" : "mdi-card-account-details-outline" }}</v-icon>
          </span>
          <span>
            <strong>{{ currentTacMembership ? "Upgrade lösen" : "Verträge & Pakete" }}</strong>
            <small>{{ currentTacMembership ? currentTacMembership.NAME : "Mitgliedschaft anzeigen" }}</small>
          </span>
          <v-icon class="customer-quick-action__arrow">mdi-chevron-right</v-icon>
        </button>
      </section>

      <nav class="customer-navigation" aria-label="Bereiche der Kundenakte">
        <button
          v-for="category in categories"
          :key="category.value"
          type="button"
          class="customer-navigation__item"
          :class="{ 'customer-navigation__item--active': activeGroup === category.value }"
          @click="activeGroup = category.value">
          <v-icon small>{{ category.icon }}</v-icon>
          <span>{{ category.text }}</span>
        </button>
      </nav>

      <main class="customer-content">
        <div v-if="loadingProfile" class="customer-loading">
          <v-progress-circular :size="56" :width="6" color="primary" indeterminate />
          <strong>Kundenakte wird geladen</strong>
        </div>

        <template v-else-if="user._id">
          <user-dashboard-customer-care v-if="activeGroup === 'care'" :user="user" />

          <section v-if="activeGroup === 'training'" class="legacy-section-shell legacy-section-shell--open">
            <user-dashboard-training-plans :user="user" />
          </section>

          <section v-if="activeGroup === 'infos'" class="legacy-section-shell">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">STAMMDATEN &amp; ZUGRIFF</span>
                <h2>Persönliche Angaben</h2>
                <p>Kontaktinformationen, Adresse und – sofern berechtigt – Rollen und Einzelrechte.</p>
              </div>
              <v-icon color="#55ca82">mdi-account-edit-outline</v-icon>
            </header>
            <user-dashboard-customer-infos :is-mobile="isMobile" :user="user" />
          </section>

          <section v-if="activeGroup === 'contracts'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">MITGLIEDSCHAFT</span>
                <h2>Verträge &amp; Pakete</h2>
                <p>Bestehende Vertragsfunktionen und deren Rechteprüfungen bleiben unverändert erhalten.</p>
              </div>
              <v-icon color="#55ca82">mdi-file-sign</v-icon>
            </header>
            <user-dashboard-contracts
              ref="contractsDashboard"
              :is-mobile="isMobile"
              :user="user"
              :initial-tac-contract-infos="tacContractInfos"
              :tac-contracts-loading="tacContractsLoading"
              @open-upgrade="openUpgrade"
              @refresh-tac-contracts="fetchTacMembership(user._id)"
              @refetchUser="refetchUser" />
          </section>

          <section v-if="activeGroup === 'interactions'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">KONTAKTHISTORIE</span>
                <h2>Dokumentierte Interaktionen</h2>
                <p>Notizen und Kontakte ausserhalb eines konkreten WorkCalendar-Termins.</p>
              </div>
              <v-icon color="#55ca82">mdi-message-text-clock-outline</v-icon>
            </header>
            <user-dashboard-interactions :key="`interactions-${user._id}-${interactionRefreshKey}`" :user="user" />
          </section>

          <section v-if="activeGroup === 'membercard'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">EINSTIEG &amp; LEAD</span>
                <h2>11-Tage-Mitgliedskarte</h2>
                <p>Aktivierung, Check-ins und Lead-Verknüpfung der Mitgliedskarte.</p>
              </div>
              <v-icon color="#55ca82">mdi-card-account-details-star-outline</v-icon>
            </header>
            <user-dashboard-membercard :user="user" />
          </section>
        </template>

        <div v-else class="customer-loading customer-loading--error">
          <v-icon color="error" size="36">mdi-account-alert-outline</v-icon>
          <strong>Kundenakte konnte nicht geladen werden</strong>
          <v-btn color="primary" depressed class="text-none" @click="refetchUser">Erneut versuchen</v-btn>
        </div>
      </main>

      <v-dialog
        v-model="showProfileImageDialog"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="900"
        content-class="customer-profile-image-dialog">
        <v-card dark class="customer-profile-image-card">
          <header class="customer-profile-image-card__header">
            <div>
              <span class="customer-eyebrow">PROFILBILD</span>
              <h3>{{ fullName }}</h3>
            </div>
            <v-btn icon aria-label="Profilbild schliessen" @click="showProfileImageDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </header>
          <div class="customer-profile-image-card__image">
            <img :src="profileImage" :alt="`Profilbild von ${fullName}`" />
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showAnamneseManagerDialog" max-width="620" content-class="customer-anamnese-dialog">
        <v-card dark class="customer-anamnese-card">
          <header class="customer-anamnese-card__header">
            <span class="customer-quick-action__icon customer-quick-action__icon--teal">
              <v-icon>mdi-clipboard-pulse-outline</v-icon>
            </span>
            <div>
              <span class="customer-eyebrow">GESUNDHEIT &amp; CHECK</span>
              <h3>Anamnesebögen</h3>
              <p>{{ fullName }}</p>
            </div>
            <v-spacer />
            <v-btn icon aria-label="Schliessen" @click="showAnamneseManagerDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </header>

          <div v-if="loadingAnamnese" class="customer-anamnese-card__state">
            <v-progress-circular indeterminate color="primary" :size="42" :width="4" />
            <span>Anamnesebögen werden geladen</span>
          </div>

          <div v-else-if="!anamneseEntries.length" class="customer-anamnese-card__state">
            <v-icon size="36">mdi-clipboard-text-off-outline</v-icon>
            <strong>Noch keine Anamnese vorhanden</strong>
            <span>Der erste Bogen kann direkt für diese Person erstellt werden.</span>
          </div>

          <div v-else class="customer-anamnese-list">
            <article v-for="(entry, index) in anamneseEntries" :key="entry._id || `anamnese-${index}`" class="customer-anamnese-entry">
              <span class="customer-anamnese-entry__date">
                <strong>{{ formatAnamneseDate(entry.createdAt, "DD") }}</strong>
                <small>{{ formatAnamneseDate(entry.createdAt, "MMM YYYY") }}</small>
              </span>
              <span class="customer-anamnese-entry__copy">
                <strong>Anamnese vom {{ formatAnamneseDate(entry.createdAt, "DD.MM.YYYY") }}</strong>
                <small v-if="entry.creator"> Erstellt von {{ `${entry.creator.vorname || ""} ${entry.creator.nachname || ""}`.trim() }} </small>
              </span>
              <v-btn icon title="Ansehen" @click="openAnamnese(entry, false)">
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
              <v-btn icon title="Bearbeiten" @click="openAnamnese(entry, true)">
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
            </article>
          </div>

          <footer class="customer-anamnese-card__footer">
            <v-btn text class="text-none" @click="showAnamneseManagerDialog = false">Schliessen</v-btn>
            <v-btn color="primary" depressed class="text-none font-weight-bold" @click="createAnamnese">
              <v-icon left>mdi-plus</v-icon>
              Neue Anamnese
            </v-btn>
          </footer>
        </v-card>
      </v-dialog>

      <customer-interaction v-if="interactionDialogMounted" ref="customerInteraction" @interactionCreated="onInteractionCreated" />
      <anamnese-bogen-create ref="anamneseBogenRef" @submitted="onAnamneseSubmitted" />
      <upgrade-contract
        v-if="user._id && showUpgradeDialog"
        :selectedCustomer="user._id"
        :dialog="showUpgradeDialog"
        @hide="showUpgradeDialog = false"
        @created="onUpgradeCreated" />
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"

import userDashboardCustomerCare from "./userDashboardCustomerCare"
import userDashboardCustomerInfos from "./userDashboardCustomerInfos"
import userDashboardMembercard from "./userDashboardMembercard"
import userDashboardContracts from "./userDashboardContracts"
import userDashboardInteractions from "./userDashboardInteractions"
import userDashboardTrainingPlans from "./userDashboardTrainingPlans"
import customerInteraction from "@/components/employee/customerInteraction.vue"
import anamneseBogenCreate from "@/components/anamneseBogenCreate"
import upgradeContract from "@/views/dashboard/contract/components/upgradeContract.vue"

export default {
  name: "UserDashboardCustomer",
  components: {
    userDashboardCustomerCare,
    userDashboardCustomerInfos,
    userDashboardMembercard,
    userDashboardContracts,
    userDashboardInteractions,
    userDashboardTrainingPlans,
    customerInteraction,
    anamneseBogenCreate,
    upgradeContract,
  },
  data() {
    return {
      baseCategories: [
        { text: "Betreuung", value: "care", icon: "mdi-account-heart-outline" },
        { text: "Training", value: "training", icon: "mdi-dumbbell" },
        { text: "Stammdaten", value: "infos", icon: "mdi-account-details-outline" },
        { text: "Verträge", value: "contracts", icon: "mdi-file-sign" },
        { text: "Interaktionen", value: "interactions", icon: "mdi-message-text-clock-outline", right: "tp_create" },
        { text: "11-Tage-Karte", value: "membercard", icon: "mdi-card-account-details-star-outline" },
      ],
      activeGroup: "care",
      userService,
      user: {},
      tacContractInfos: [],
      tacContractsLoading: false,
      loadingProfile: false,
      interactionDialogMounted: false,
      interactionRefreshKey: 0,
      showAnamneseManagerDialog: false,
      loadingAnamnese: false,
      anamneseEntries: [],
      showUpgradeDialog: false,
      showProfileImageDialog: false,
      handledRouteIntent: "",
    }
  },
  computed: {
    categories() {
      return this.baseCategories.filter((category) => {
        if (category.right && !this.userService.hasRight(category.right)) return false
        if (["care", "training"].includes(category.value) && this.user.hasInternalAccess) return false
        return true
      })
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    canCreateInteraction() {
      return this.userService.hasRight("tp_create")
    },
    canManageContracts() {
      return this.userService.hasRight("c_create_customer")
    },
    canManageAppointments() {
      return this.userService.hasRight("sm_appointment_management")
    },
    currentTacMembership() {
      const now = this.$moment()
      return (
        this.tacContractInfos.find((contract) => {
          const contractType = String(contract?.CONTRACTTYP || "").trim()
          const groupName = String(contract?.GROUP_NAME || "").trim()
          const start = this.$moment(contract?.TERM_STARTDATE)
          const end = this.$moment(contract?.TERM_ENDDATE)
          const isMembership = contractType === "Hauptvertrag" || (contractType === "Bonusvertrag" && groupName !== "Zusatzpakete")
          return (
            isMembership &&
            contract?.CONTRACTSTATE === "Laufend" &&
            start.isValid() &&
            end.isValid() &&
            now.isSameOrAfter(start, "day") &&
            now.isSameOrBefore(end, "day")
          )
        }) || null
      )
    },
    fullName() {
      const name = `${this.user.vorname || ""} ${this.user.nachname || ""}`.trim()
      return name || this.user.email || "Unbekannter Benutzer"
    },
    profileImage() {
      if (this.user.bild) return `/img/profilbilder${this.user.bild}`
      return `https://app.wellcomefit.ch/api/checkin/pool/images/${this.user._id}`
    },
    tacMembershipStart() {
      const contracts = Array.isArray(this.tacContractInfos) ? this.tacContractInfos : []
      const memberships = contracts.filter((contract) => {
        const type = String(contract && contract.CONTRACTTYP ? contract.CONTRACTTYP : "")
          .trim()
          .toLocaleLowerCase()
        const group = String(contract && contract.GROUP_NAME ? contract.GROUP_NAME : "")
          .trim()
          .toLocaleLowerCase()

        return type === "hauptvertrag" || (type === "bonusvertrag" && group !== "zusatzpakete")
      })
      const candidates = memberships.length ? memberships : contracts
      const validDates = candidates.map((contract) => this.$moment(contract && contract.TERM_STARTDATE)).filter((date) => date.isValid())

      if (!validDates.length) return null
      return validDates.reduce((earliest, date) => (date.isBefore(earliest) ? date : earliest))
    },
    membercardStatus() {
      const membercard = this.user && this.user.membercard
      if (!membercard || !membercard.startDate) return null

      const rawEndDate = membercard.endDate
      const endDate = rawEndDate ? this.$moment(rawEndDate) : null
      if (!endDate || !endDate.isValid()) {
        return {
          label: "Mitgliedskarte prüfen",
          tone: "warning",
          icon: "mdi-card-alert-outline",
        }
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(String(rawEndDate))) endDate.endOf("day")
      if (endDate.isBefore(this.$moment())) {
        return {
          label: "Mitgliedskarte abgelaufen",
          tone: "expired",
          icon: "mdi-card-off-outline",
        }
      }

      if (!membercard.isActive) {
        return {
          label: "Mitgliedskarte pausiert",
          tone: "muted",
          icon: "mdi-pause-circle-outline",
        }
      }

      return {
        label: "Mitgliedskarte aktiv",
        tone: "accent",
        icon: "mdi-card-account-details-star-outline",
      }
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        if (id) {
          this.activeGroup = this.$route.query.section === "training" ? "training" : "care"
          this.fetchUser(id)
        }
      },
    },
  },
  methods: {
    openProfileImage() {
      if (this.user?._id) this.showProfileImageDialog = true
    },
    refetchUser() {
      if (this.$route.params.id) this.fetchUser(this.$route.params.id)
    },
    async fetchUser(id) {
      this.loadingProfile = true
      this.tacContractInfos = []
      this.tacContractsLoading = false
      this.showAnamneseManagerDialog = false
      this.anamneseEntries = []
      this.showUpgradeDialog = false
      this.showProfileImageDialog = false
      this.interactionDialogMounted = false
      try {
        this.user = await api.getCustomerDashboard(id)
        if (this.user.hasInternalAccess && ["care", "training"].includes(this.activeGroup)) this.activeGroup = "infos"
        this.applyRouteIntent()
        this.fetchTacMembership(id)
      } catch (error) {
        this.user = {}
        this.$toast.error("Kundenakte konnte nicht geladen werden")
      } finally {
        this.loadingProfile = false
      }
    },
    applyRouteIntent() {
      const section = String(this.$route.query.section || "")
      const action = String(this.$route.query.action || "")
      const intentKey = `${this.user?._id || ""}:${section}:${action}`

      if (section === "training" && !this.user.hasInternalAccess) this.activeGroup = "training"
      if (!action || this.handledRouteIntent === intentKey) return
      this.handledRouteIntent = intentKey

      if (action === "anamnese" && !this.user.hasInternalAccess) {
        this.$nextTick(() => this.openAnamneseManager())
      }
    },
    async fetchTacMembership(id) {
      this.tacContractsLoading = true
      try {
        const contracts = await api.fetchTACContractDetails(id)
        if (String(this.$route.params.id || "") !== String(id || "")) return
        this.tacContractInfos = Array.isArray(contracts) ? contracts : []
      } catch (error) {
        if (String(this.$route.params.id || "") === String(id || "")) this.tacContractInfos = []
      } finally {
        if (String(this.$route.params.id || "") === String(id || "")) this.tacContractsLoading = false
      }
    },
    openInteraction() {
      if (!this.canCreateInteraction || !this.user?._id) return
      this.interactionDialogMounted = true
      this.$nextTick(() => {
        const dialog = this.$refs.customerInteraction
        if (dialog && typeof dialog.setVisible === "function") dialog.setVisible(this.user._id)
      })
    },
    onInteractionCreated() {
      this.interactionRefreshKey += 1
      this.$toast.success("Interaktion wurde erfasst")
    },
    planAppointment() {
      if (!this.canManageAppointments || !this.user?._id) return
      this.$router.push({
        path: "/work-calendar",
        query: {
          createAppointmentFor: this.user._id,
          date: this.$moment().format("YYYY-MM-DD"),
        },
      })
    },
    async openAnamneseManager() {
      if (!this.user?._id) return
      this.showAnamneseManagerDialog = true
      this.loadingAnamnese = true
      try {
        const documents = await api.getCustomerDocuments(this.user._id)
        this.anamneseEntries = Array.isArray(documents?.medicalHistory) ? documents.medicalHistory : []
      } catch (error) {
        this.anamneseEntries = []
        this.$toast.error("Anamnesebögen konnten nicht geladen werden")
      } finally {
        this.loadingAnamnese = false
      }
    },
    formatAnamneseDate(value, format) {
      const date = this.$moment(value)
      return date.isValid() ? date.format(format) : "–"
    },
    openAnamnese(entry, editMode) {
      if (!entry?._id) return
      this.showAnamneseManagerDialog = false
      this.$nextTick(() => {
        const dialog = this.$refs.anamneseBogenRef
        if (dialog && typeof dialog.setVisible === "function") dialog.setVisible(entry._id, editMode)
      })
    },
    createAnamnese() {
      if (!this.user?._id) return
      this.showAnamneseManagerDialog = false
      this.$nextTick(() => {
        const dialog = this.$refs.anamneseBogenRef
        if (dialog && typeof dialog.setVisible === "function") {
          dialog.setVisible(null, false, false, this.user._id)
        }
      })
    },
    onAnamneseSubmitted(userId) {
      this.anamneseEntries = []
      if (this.userService.hasRight("ab_create") && (userId || this.user?._id)) {
        this.$router.push(`/gfrecommendations/${userId || this.user._id}`)
      }
    },
    openMembershipAction() {
      if (this.currentTacMembership) {
        this.openUpgrade()
        return
      }
      this.activeGroup = "contracts"
    },
    openUpgrade() {
      if (!this.user?._id || !this.canManageContracts) return
      this.showUpgradeDialog = true
    },
    async onUpgradeCreated() {
      this.showUpgradeDialog = false
      this.$toast.success("Upgrade wurde erfasst")
      await this.fetchTacMembership(this.user._id)
      const contractsDashboard = this.$refs.contractsDashboard
      if (contractsDashboard && typeof contractsDashboard.refreshExternalData === "function") {
        contractsDashboard.refreshExternalData()
      }
    },
    async deleteUser() {
      if (!this.userService.hasRight("u_delete")) return
      const confirmed = window.confirm(
        `Soll der Benutzer ${this.fullName} wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.`,
      )
      if (!confirmed || !window.confirm("Ganz sicher?")) return

      try {
        const result = await api.deleteUser({ benutzer_id: this.user._id })
        if (!result || !result.status) throw new Error("delete_failed")
        this.$toast.success("Benutzer wurde gelöscht")
        this.$router.push("/dashboard/user/search")
      } catch (error) {
        this.$toast.error("Benutzer konnte nicht gelöscht werden")
      }
    },
  },
}
</script>

<style scoped>
.customer-profile-page {
  --cp-primary: #009640;
  --cp-primary-soft: rgba(0, 150, 64, 0.14);
  --cp-surface: rgba(0, 0, 0, 0.2);
  --cp-surface-raised: rgba(255, 255, 255, 0.045);
  --cp-border: rgba(255, 255, 255, 0.1);
  --cp-text: #f6f7f6;
  --cp-muted: #a6aaa7;
  min-height: 100%;
  padding: clamp(14px, 2.4vw, 34px);
  color: var(--cp-text);
  background: transparent;
}

.customer-profile-shell {
  width: 100%;
  max-width: 1540px;
  margin: 0 auto;
}

.customer-hero {
  position: relative;
  display: flex;
  min-height: 164px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 30px 26px 82px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 25px;
  background: radial-gradient(circle at 92% 3%, rgba(0, 150, 64, 0.16), transparent 34%),
    linear-gradient(110deg, rgba(0, 0, 0, 0.32), rgba(255, 255, 255, 0.025)), var(--cp-surface);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.23);
  backdrop-filter: blur(18px);
}

.customer-hero::after {
  position: absolute;
  top: -148px;
  right: -52px;
  width: 360px;
  height: 360px;
  border: 1px solid rgba(0, 150, 64, 0.13);
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.customer-hero__back {
  position: absolute;
  z-index: 2;
  top: 24px;
  left: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
}

.customer-hero__back .v-icon {
  color: #d9dedb !important;
}

.customer-hero__identity {
  z-index: 1;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 20px;
}

.customer-hero__avatar {
  position: relative;
  flex: 0 0 auto;
  border: 3px solid rgba(101, 202, 139, 0.32);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.3);
}

.customer-hero__avatar img {
  object-fit: cover;
}

.customer-hero__avatar--interactive {
  cursor: zoom-in;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.customer-hero__avatar--interactive:hover,
.customer-hero__avatar--interactive:focus-visible {
  border-color: rgba(101, 202, 139, 0.72);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.35), 0 0 0 4px rgba(0, 150, 64, 0.12);
  outline: none;
  transform: translateY(-1px);
}

.customer-hero__avatar-expand {
  position: absolute;
  right: 3px;
  bottom: 3px;
  display: inline-flex;
  width: 23px;
  height: 23px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.72);
  pointer-events: none;
}

.customer-hero__avatar-expand .v-icon {
  color: inherit !important;
}

.customer-hero__copy {
  min-width: 0;
}

.customer-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.13em;
  line-height: 1.2;
}

.customer-hero h1 {
  overflow: hidden;
  margin: 0;
  color: #ffffff;
  font-size: clamp(28px, 3.1vw, 42px);
  font-weight: 780;
  letter-spacing: -0.04em;
  line-height: 1.08;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-hero__meta,
.customer-hero__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 10px;
}

.customer-hero__meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #b8bcb9;
  font-size: 13px;
}

.customer-hero__meta .v-icon {
  color: #aeb5b0 !important;
}

.identity-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  color: #b4b9b5;
  background: rgba(255, 255, 255, 0.035);
  font-size: 12px;
  font-weight: 700;
}

.identity-badge .v-icon {
  color: inherit !important;
}

.identity-badge--success {
  border-color: rgba(0, 150, 64, 0.27);
  color: #7fd7a1;
  background: rgba(0, 150, 64, 0.1);
}

.identity-badge--accent {
  border-color: rgba(37, 135, 232, 0.3);
  color: #78baf6;
  background: rgba(37, 135, 232, 0.1);
}

.identity-badge--expired {
  border-color: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
}

.identity-badge--warning {
  border-color: rgba(245, 158, 11, 0.3);
  color: #f3c568;
  background: rgba(245, 158, 11, 0.1);
}

.identity-badge--muted {
  border-color: rgba(148, 163, 184, 0.23);
  color: #bdc4cb;
  background: rgba(148, 163, 184, 0.09);
}

.customer-hero__actions {
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  gap: 7px;
}

.customer-hero__actions .v-btn {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.045);
}

.customer-hero__actions .v-btn .v-icon {
  color: #d9dedb !important;
}

.customer-hero__actions .v-btn.error--text .v-icon {
  color: #ff6b72 !important;
}

.customer-quick-actions {
  display: grid;
  gap: 10px;
  margin-top: 15px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.customer-quick-action {
  display: grid;
  min-width: 0;
  min-height: 72px;
  align-items: center;
  gap: 11px;
  padding: 11px 13px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  color: #f4f7f5;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.11)), rgba(0, 0, 0, 0.13);
  cursor: pointer;
  font: inherit;
  grid-template-columns: 42px minmax(0, 1fr) 24px;
  text-align: left;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.customer-quick-action:hover {
  border-color: rgba(101, 202, 139, 0.28);
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.08), rgba(0, 0, 0, 0.11)), rgba(0, 0, 0, 0.13);
  transform: translateY(-1px);
}

.customer-quick-action > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.customer-quick-action strong,
.customer-quick-action small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-quick-action strong {
  color: #f8faf9;
  font-size: 13px;
}

.customer-quick-action small {
  color: #969f99;
  font-size: 12px;
}

.customer-quick-action__icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #72d899;
  background: rgba(0, 150, 64, 0.13);
}

.customer-quick-action__icon .v-icon {
  color: inherit !important;
}

.customer-quick-action__icon--blue {
  color: #73baf1;
  background: rgba(44, 137, 218, 0.13);
}

.customer-quick-action__icon--green {
  color: #55ca82;
  background: rgba(0, 150, 64, 0.16);
}

.customer-quick-action__icon--purple {
  color: #c9a0f4;
  background: rgba(160, 86, 224, 0.13);
}

.customer-quick-action__icon--teal {
  color: #61d2c4;
  background: rgba(27, 171, 155, 0.13);
}

.customer-quick-action__icon--amber {
  color: #efbd69;
  background: rgba(224, 145, 27, 0.13);
}

.customer-quick-action__arrow {
  color: #78817b !important;
}

.customer-navigation {
  display: flex;
  margin-top: 15px;
  padding: 7px;
  overflow-x: auto;
  border: 1px solid var(--cp-border);
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.18);
  scrollbar-width: thin;
}

.customer-navigation__item {
  position: relative;
  display: inline-flex;
  min-height: 44px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 17px;
  border: 0;
  border-radius: 12px;
  color: #9fa5a1;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 750;
  transition: color 160ms ease, background 160ms ease;
}

.customer-navigation__item .v-icon {
  color: #a7ada9 !important;
}

.customer-navigation__item:hover {
  color: #d9dcda;
  background: rgba(255, 255, 255, 0.035);
}

.customer-navigation__item--active {
  color: #81d9a3;
  background: var(--cp-primary-soft);
}

.customer-navigation__item--active .v-icon {
  color: #65ca8b !important;
}

.customer-content {
  margin-top: 15px;
}

.customer-loading {
  display: flex;
  min-height: 420px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  border: 1px solid var(--cp-border);
  border-radius: 22px;
  background: var(--cp-surface);
}

.customer-loading strong {
  font-size: 16px;
}

.customer-loading--error {
  color: #fca5a5;
}

.legacy-section-shell {
  overflow: hidden;
  padding: 24px;
  border: 1px solid var(--cp-border);
  border-radius: 22px;
  background: linear-gradient(145deg, var(--cp-surface-raised), rgba(0, 0, 0, 0.17));
}

.legacy-section-shell--open {
  padding: 0 0 24px;
}

.section-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: -24px -24px 20px;
  padding: 24px;
  border-bottom: 1px solid var(--cp-border);
  background: rgba(255, 255, 255, 0.018);
}

.legacy-section-shell--open .section-intro {
  margin: 0 0 4px;
}

.section-intro h2 {
  margin: 0;
  color: #ffffff;
  font-size: 23px;
  letter-spacing: -0.03em;
}

.section-intro p {
  margin: 6px 0 0;
  color: var(--cp-muted);
  font-size: 13px;
}

.section-intro > .v-icon {
  color: #65ca8b !important;
}

.legacy-section-shell ::v-deep .v-card:not(.contract-card) {
  color: #f6f7f6;
  background: transparent !important;
  box-shadow: none !important;
}

.legacy-section-shell ::v-deep .v-data-table,
.legacy-section-shell ::v-deep .v-sheet:not(.contract-card) {
  color: #f6f7f6;
  background: transparent !important;
}

.legacy-section-shell ::v-deep .v-input,
.legacy-section-shell ::v-deep .v-label,
.legacy-section-shell ::v-deep .v-card__text,
.legacy-section-shell ::v-deep .v-card__subtitle,
.legacy-section-shell ::v-deep td,
.legacy-section-shell ::v-deep th {
  font-size: 12px !important;
}

.customer-profile-image-card {
  display: flex;
  max-height: calc(100vh - 48px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 22px !important;
  background: #111513 !important;
}

.customer-profile-image-card__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 17px 18px 15px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.customer-profile-image-card__header h3 {
  margin: 0;
  color: #ffffff;
  font-size: 20px;
  letter-spacing: -0.02em;
}

.customer-profile-image-card__header .v-btn .v-icon {
  color: #d3d9d5 !important;
}

.customer-profile-image-card__image {
  display: flex;
  min-height: 280px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(circle at 50% 15%, rgba(0, 150, 64, 0.1), transparent 45%), #090c0a;
}

.customer-profile-image-card__image img {
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 145px);
  object-fit: contain;
}

.customer-anamnese-card {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px !important;
  background: radial-gradient(circle at 96% 0%, rgba(27, 171, 155, 0.12), transparent 34%), #1b1f1d !important;
}

.customer-anamnese-card__header {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.customer-anamnese-card__header h3 {
  margin: 0;
  color: #ffffff;
  font-size: 21px;
}

.customer-anamnese-card__header p {
  margin: 3px 0 0;
  color: #9fa7a1;
  font-size: 12px;
}

.customer-anamnese-card__header .v-btn .v-icon {
  color: #c8cfca !important;
}

.customer-anamnese-card__state {
  display: flex;
  min-height: 250px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 24px;
  color: #939c96;
  text-align: center;
}

.customer-anamnese-card__state strong {
  color: #ffffff;
  font-size: 16px;
}

.customer-anamnese-card__state span {
  font-size: 12px;
}

.customer-anamnese-card__state > .v-icon {
  color: #61d2c4 !important;
}

.customer-anamnese-list {
  display: grid;
  max-height: 430px;
  gap: 8px;
  padding: 15px 18px;
  overflow-y: auto;
}

.customer-anamnese-entry {
  display: grid;
  min-height: 70px;
  align-items: center;
  gap: 10px;
  padding: 10px 11px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.03);
  grid-template-columns: 58px minmax(0, 1fr) 36px 36px;
}

.customer-anamnese-entry__date,
.customer-anamnese-entry__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.customer-anamnese-entry__date {
  align-items: center;
  padding-right: 10px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.customer-anamnese-entry__date strong {
  color: #ffffff;
  font-size: 20px;
  line-height: 1;
}

.customer-anamnese-entry__date small,
.customer-anamnese-entry__copy small {
  color: #8e9891;
  font-size: 12px;
}

.customer-anamnese-entry__copy strong {
  overflow: hidden;
  color: #eff3f0;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-anamnese-entry__copy small {
  margin-top: 4px;
}

.customer-anamnese-entry .v-btn .v-icon {
  color: #aeb7b0 !important;
}

.customer-anamnese-card__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 18px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

@media (max-width: 860px) {
  .customer-hero {
    align-items: flex-start;
    padding: 72px 20px 22px;
  }

  .customer-hero__identity {
    align-items: flex-start;
  }

  .customer-hero h1 {
    font-size: 27px;
    white-space: normal;
  }

  .customer-hero__meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }

  .customer-hero__actions {
    position: absolute;
    top: 20px;
    right: 18px;
  }

  .customer-quick-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .customer-profile-page {
    padding: 10px;
  }

  .customer-hero {
    min-height: 0;
    padding: 76px 18px 22px;
  }

  .customer-hero__identity {
    width: 100%;
    align-items: flex-start;
    gap: 14px;
  }

  .customer-hero__avatar {
    position: relative;
    margin-top: 2px;
  }

  .customer-hero__copy {
    min-width: 0;
    width: 100%;
  }

  .customer-hero h1 {
    font-size: 24px;
    line-height: 1.12;
  }

  .customer-hero__meta span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .customer-navigation__item {
    min-height: 42px;
    padding: 0 13px;
  }

  .customer-quick-actions {
    gap: 8px;
    grid-template-columns: 1fr;
  }

  .customer-quick-action {
    min-height: 66px;
  }

  .legacy-section-shell {
    padding: 16px;
  }

  .section-intro {
    margin: -16px -16px 16px;
    padding: 18px 16px;
  }

  .customer-anamnese-entry {
    grid-template-columns: 50px minmax(0, 1fr) 32px 32px;
  }

  .customer-profile-image-card {
    max-height: none;
    min-height: 100%;
    border: 0;
    border-radius: 0 !important;
  }

  .customer-profile-image-card__image {
    min-height: 0;
    flex: 1 1 auto;
  }

  .customer-profile-image-card__image img {
    max-height: calc(100vh - 78px);
  }
}
</style>
